<template>
  <BCard
      class="mt-3 text-start"
      :title="event.name"
      role="button"

      :style="{ borderColor: selected ? buttonBG : 'transparent', borderWidth: selected ? '2px' : '1px', borderStyle: 'solid' }"
  >
    <template #img>
      <BCardImg :src="getImageUrl(event)" class="card-img"></BCardImg>
    </template>
    <small class="position-relative event-text d-flex align-items-center">
      <small
          class="status p-2 rounded-circle me-2"
          :class="sessionAvailabilityClass(event)"
      />
      {{ sessionAvailabilityText(event) }}
      {{ singleSessionTimeEnabled ? singleSessionTime(event) : '' }} <!-- Only shown if event has only one session and is enabled -->
    </small>

    <!-- Event Description-->
    <details
        v-if="showEventCardDescription && event.description"
        class="event-description mt-3"
    >
      <summary class="mb-3">Description</summary>

      <div
          class="event-description__content mt-2"
          v-html="eventDescription.html"
      ></div>

      <button
          v-if="eventDescription.truncated"
          type="button"
          class="btn btn-link event-description__read-more p-0 mt-2"
          @click.stop="showDescriptionModal = true"
      >
        Read full description
      </button>
    </details>

    <!-- Full event description modal -->
    <div
        v-if="showDescriptionModal"
        class="event-description-modal"
        role="dialog"
        aria-modal="true"
        :aria-label="`${event.name} description`"
        @click.self="showDescriptionModal = false"
    >
      <div class="event-description-modal__dialog">
        <div class="event-description-modal__header">
          <h5 class="mb-0">{{ event.name }}</h5>

          <button
              type="button"
              class="event-description-modal__close"
              aria-label="Close description"
              @click="showDescriptionModal = false"
          >
            &times;
          </button>
        </div>

        <div
            class="event-description-modal__content"
            v-html="fullEventDescription"
        ></div>
      </div>
    </div>

    <div v-if="showPricing">
      <hr/>
      <TicketPricing
          :tickets="tickets"
          :event="event"
      />
    </div>
  </BCard>
</template>

<script setup>
import {thumbImage} from "../functions/imageFunctions";
import TicketPricing from "./TicketPricing.vue";
import {BCard} from "bootstrap-vue-next";
import {computed, ref} from "vue";

const props = defineProps({
  event: {
    type: Object,
    required: true,
  },
  buttonBG: {
    type: String,
    required: true,
  },
  tickets: {
    type: Array,
    required: true,
  },
  showPricing: {
    type: Boolean,
    required: false,
    default: false,
  },
  selected: {
    type: Boolean,
    required: false,
    default: false,
  },
  lowCapacityThreshold: {
    type: Number,
    default: null,
    required: false,
  },
  singleSessionTimeEnabled: {
    type: Boolean,
    default: false,
  },
  showEventCardDescription: {
    type: Boolean,
    default: false,
  }
});

const sessionAvailabilityClass = (event) => {
  if (event.sessions.every((s) => s.spacesAvailable <= 0)) {
    return 'bg-danger';
  }

  if (event.sessions.every((s) => s.spacesAvailable < props.lowCapacityThreshold)) {
    return 'bg-warning';
  }

  return 'bg-success';
}

const sessionAvailabilityText = (event) => {
  if (event.sessions.every((s) => s.spacesAvailable === 0)) {
    return 'Sold Out';
  }

  if (event.sessions.every((s) => s.spacesAvailable < props.lowCapacityThreshold)) {
    return 'Low Availability';
  }

  return 'Tickets Available';
}

// Only outputs if event has only one session
const singleSessionTime = (event) => {
  if (event.sessions.length !== 1) {
    return '';
  }

  const session = event.sessions[0];

  if (session.spacesAvailable > 0 && session.time) {
    return `at ${session.time.slice(0, 5)}`;
  }

  return '';
}

const getImageUrl = (event) => {
  // console.log('Button background color:', props.buttonBG);
  if (event['imageUrl']) {
    return thumbImage(event['imageUrl'], 420, 300, false);
  }

  return thumbImage('https://app.digitickets.co.uk/images/frontend/holding.png', 420, 300, false);
}

// ------------------------
// Event Description code
// ------------------------
const descriptionMaxLength = 500;
const eventDescription = computed(() => getEventDescription(props.event));

// Full event description shown in model if descriptionMaxLength reached
const showDescriptionModal = ref(false);
const fullEventDescription = computed(() => getFullEventDescription(props.event));

// Gets event description, truncates if required, then reformats headings
const getEventDescription = (event) => {
  const html = event['description'] || '';

  if (!html) {
    return {
      html: '',
      truncated: false,
    };
  }

  const container = document.createElement('div');
  container.innerHTML = html;

  const textLength = (container.textContent || '').length;
  const truncated = textLength > descriptionMaxLength;

  if (truncated) {
    truncateHtml(container, descriptionMaxLength);
  }

  normaliseHeadings(container);

  return {
    html: container.innerHTML,
    truncated,
  };
}

const getFullEventDescription = (event) => {
  const html = event['description'] || '';

  if (!html) {
    return '';
  }

  const container = document.createElement('div');
  container.innerHTML = html;

  normaliseHeadings(container);

  return container.innerHTML;
}


// Helper for getEventDescription()
const truncateHtml = (container, maxLength) => {
  let currentLength = 0;
  let finished = false;

  const truncateNode = (node) => {
    if (finished) {
      node.remove();
      return;
    }

    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent;
      const remaining = maxLength - currentLength;

      if (text.length > remaining) {
        node.textContent = `${text.substring(0, remaining).trim()}…`;
        finished = true;
        return;
      }

      currentLength += text.length;
      return;
    }

    [...node.childNodes].forEach((child) => {
      truncateNode(child);
    });
  };

  [...container.childNodes].forEach((child) => {
    truncateNode(child);
  });

  // Remove any empty elements left after truncation.
  container.querySelectorAll('*').forEach((element) => {
    if (
        !element.textContent.trim() &&
        !['BR', 'IMG'].includes(element.tagName)
    ) {
      element.remove();
    }
  });
}

// Helper for getEventDescription()
const normaliseHeadings = (container) => {
  const headingMap = {
    h1: 'event-description__heading-1',
    h2: 'event-description__heading-2',
    h3: 'event-description__heading-3',
  };

  Object.entries(headingMap).forEach(([tag, className]) => {
    container.querySelectorAll(tag).forEach((heading) => {
      const replacement = document.createElement('p');

      replacement.className = className;
      replacement.innerHTML = heading.innerHTML;

      heading.replaceWith(replacement);
    });
  });
}
</script>

<style scoped>
.status {
  height: 10px;
  width: 10px;
  display: inline-block;
}


</style>

<style lang="scss">
.digi-booking {
  .card {
    border-radius: 6px;
    border: none;
    background: #FFF;
    box-shadow: 0 0 6px 2px rgba(0, 0, 0, 0.08);
    padding: 12px 8px;
    .card-img {
      aspect-ratio: 420 / 300; // prevents layout shift
      background-color: #d4d4d4;  // grey placeholder
      border-radius: 4px;
      object-fit: cover;
    }
    .card-body {
      padding: 10px 10px 0 10px;

      h4.card-title {
        font-size: 18px;
        font-style: normal;
        font-weight: 800;
      }

      .event-description {
        &__heading-1 {
          font-size: 20px;
          font-weight: 700;
          margin-bottom: 10px;
        }
        &__heading-2 {
          font-size: 18px;
          font-weight: 600;
          margin-bottom: 8px;
        }
        &__heading-3 {
          font-size: 16px;
          font-weight: 600;
          margin-bottom: 8px;
        }
      }
    }
  }

  .event-description-modal {
    position: fixed;
    inset: 0;
    z-index: 1055;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px;
    background: rgba(0, 0, 0, 0.5);

    &__dialog {
      width: min(100%, 640px);
      max-height: 85vh;
      overflow: hidden;
      display: flex;
      flex-direction: column;
      background: #fff;
      border-radius: 8px;
      box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
    }

    &__header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 16px;
      padding: 16px 20px;
      border-bottom: 1px solid #e5e5e5;
    }

    &__close {
      border: 0;
      background: transparent;
      font-size: 28px;
      line-height: 1;
      padding: 0;
      cursor: pointer;
    }

    &__content {
      overflow-y: auto;
      padding: 20px;

      p:last-child,
      ul:last-child,
      ol:last-child {
        margin-bottom: 0;
      }
    }
  }
}
</style>
